import { useEffect, useState } from "react";
import { Tag } from "antd";
import { Clock } from "lucide-react";

const phaseLabels: Record<number, string> = {
  1: "Ignition",
  2: "Data Gathering",
  3: "Synthesis",
  4: "Peak Ambition",
  5: "Objectives",
  6: "Gap Filling",
  7: "Draft Ready",
};

const PHASE_2_TARGET_SECONDS = 4 * 60;
// Mirror the isResearchingNow guard in RoadmapBuilderSession: research is only
// "active" for 10 min after it starts, so the timer can never run forever.
const RESEARCH_TIMEOUT_MS = 10 * 60 * 1000;

function formatTime(totalSeconds: number): string {
  const mins = Math.floor(totalSeconds / 60);
  const secs = totalSeconds % 60;
  return `${mins}:${secs.toString().padStart(2, "0")}`;
}

interface PhaseIndicatorProps {
  phase: number;
  sessionStartedAt?: string | null;
  researchStartedAt?: string | null;
  researchComplete?: boolean;
}

export function PhaseIndicator({ phase, sessionStartedAt, researchStartedAt, researchComplete }: PhaseIndicatorProps) {
  const label = phaseLabels[phase] || "In Progress";
  const [elapsed, setElapsed] = useState(0);

  // Anchor the clock to when research actually started, not when the session was
  // created. sessionStartedAt is immutable, so anchoring to it made a session opened
  // 44 min after creation read "44:xx". researchStartedAt is refreshed on each research
  // trigger; fall back to sessionStartedAt only when it is absent.
  const refTime = researchStartedAt ?? sessionStartedAt;
  const startTime = refTime ? new Date(refTime).getTime() : 0;

  // Self-terminating: hide the clock once research completes OR the 10-min research
  // window has elapsed, so a stuck session (research_complete never flips true) can't
  // keep the timer ticking forever.
  const showTimer =
    phase === 2 &&
    !!refTime &&
    !researchComplete &&
    Date.now() - startTime < RESEARCH_TIMEOUT_MS;

  useEffect(() => {
    if (!showTimer) return;
    const update = () => {
      setElapsed(Math.max(0, Math.floor((Date.now() - startTime) / 1000)));
    };
    update();
    const interval = setInterval(update, 1000);
    return () => clearInterval(interval);
  }, [showTimer, startTime]);

  if (showTimer) {
    return (
      <Tag style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12 }} data-testid="badge-phase">
        <span className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse" />
        Phase {phase}: {label}
        <Clock className="w-3 h-3 ml-1" />
        <span data-testid="text-phase-timer">
          {formatTime(elapsed)} / ~{formatTime(PHASE_2_TARGET_SECONDS)}
        </span>
      </Tag>
    );
  }

  return (
    <Tag style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12 }} data-testid="badge-phase">
      <span className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse" />
      Phase {phase}: {label}
    </Tag>
  );
}
